import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { IUser } from '../../UserSlice';
import { ISubscriptionPlan } from '../SubscriptionPlansSlice';
import { IStudentFlexibleRateVersion } from '../StudentFlexibleRateVersionsSlice';
import { RootState } from '../..';
import {
  adminCreateStudentSubscriptionRate,
  adminDeleteStudentSubscriptionRate,
  adminGetStudentSubscriptionRates,
  adminUpdateStudentSubscriptionRate,
  getUserSubscriptionRatesAsAdmin,
} from './adminApi';

export interface IStudentSubscriptionRateAmount {
  studentSubscriptionRateVersion: string | IStudentFlexibleRateVersion;
  amount: number;
  currency: string;
}

export interface IStudentSubscriptionRate {
  _id: string;
  package: string | ISubscriptionPlan;
  amounts: Array<IStudentSubscriptionRateAmount>;
  createdBy: string | IUser;
  updatedBy: string | IUser;
  country?: string;
  curriculum?: string;
  grade?: string;
}

export interface IInitialStudentSubscriptionRatesState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminStudentSubscriptionRateListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IStudentSubscriptionRate>;
  };
  adminUserSubscriptionRates: IStudentSubscriptionRate[];
}

const initialState: IInitialStudentSubscriptionRatesState = {
  status: 'idle',
  // STUDENT SUBSCRIPTION RATES
  adminStudentSubscriptionRateListData: undefined,
  adminUserSubscriptionRates: [],
};

export const StudentSubscriptionRatesSlice = createSlice({
  initialState,
  name: 'studentSubscriptionRatesSlice',
  reducers: {
    resetAdminStudentSubscriptionRateListData: (state) => {
      state.adminStudentSubscriptionRateListData = undefined;
    },
    resetAdminUserSubscriptionRates: (state) => {
      state.adminUserSubscriptionRates = [];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(adminGetStudentSubscriptionRates.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminStudentSubscriptionRateListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminStudentSubscriptionRateListData = newAdminStudentSubscriptionRateListData;
      })
      .addCase(
        adminCreateStudentSubscriptionRate.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (state.adminStudentSubscriptionRateListData) {
            const newAdminStudentSubscriptionRateListData = {
              ...state.adminStudentSubscriptionRateListData,
            };

            newAdminStudentSubscriptionRateListData.list.unshift(result);

            if (newAdminStudentSubscriptionRateListData.totalDocs) {
              newAdminStudentSubscriptionRateListData.totalDocs += 1;
            } else {
              newAdminStudentSubscriptionRateListData.totalDocs = 1;
            }

            state.adminStudentSubscriptionRateListData = newAdminStudentSubscriptionRateListData;
          }
        }
      )
      .addCase(
        adminUpdateStudentSubscriptionRate.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminStudentSubscriptionRateListData &&
            state.adminStudentSubscriptionRateListData.list &&
            state.adminStudentSubscriptionRateListData.list.length
          ) {
            const newAdminStudentSubscriptionRateListData = {
              ...state.adminStudentSubscriptionRateListData,
            };

            const index = newAdminStudentSubscriptionRateListData.list.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newAdminStudentSubscriptionRateListData.list[index] = result;
            }

            state.adminStudentSubscriptionRateListData = newAdminStudentSubscriptionRateListData;
          }
        }
      )
      .addCase(
        adminDeleteStudentSubscriptionRate.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminStudentSubscriptionRateListData &&
            state.adminStudentSubscriptionRateListData.list &&
            state.adminStudentSubscriptionRateListData.list.length
          ) {
            const newAdminStudentSubscriptionRateListData = {
              ...state.adminStudentSubscriptionRateListData,
            };

            newAdminStudentSubscriptionRateListData.totalDocs = newAdminStudentSubscriptionRateListData.totalDocs
              ? newAdminStudentSubscriptionRateListData.totalDocs - 1
              : 0;

            newAdminStudentSubscriptionRateListData.list = newAdminStudentSubscriptionRateListData.list.filter(
              (item) => item._id !== result._id
            );

            state.adminStudentSubscriptionRateListData = newAdminStudentSubscriptionRateListData;
          }
        }
      )
      .addCase(getUserSubscriptionRatesAsAdmin.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminUserSubscriptionRates = result || [];
      });

    // Common Matchers
    builder.addMatcher(
      isPending(
        adminCreateStudentSubscriptionRate,
        adminGetStudentSubscriptionRates,
        adminUpdateStudentSubscriptionRate,
        adminDeleteStudentSubscriptionRate,
        getUserSubscriptionRatesAsAdmin
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        adminCreateStudentSubscriptionRate,
        adminGetStudentSubscriptionRates,
        adminUpdateStudentSubscriptionRate,
        adminDeleteStudentSubscriptionRate,
        getUserSubscriptionRatesAsAdmin
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        adminCreateStudentSubscriptionRate,
        adminGetStudentSubscriptionRates,
        adminUpdateStudentSubscriptionRate,
        adminDeleteStudentSubscriptionRate,
        getUserSubscriptionRatesAsAdmin
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getStudentSubscriptionRatesState = (
  state: RootState
): RootState['studentSubscriptionRatesState'] =>
  state.studentSubscriptionRatesState;

export const {
  resetAdminStudentSubscriptionRateListData,
  resetAdminUserSubscriptionRates,
} = StudentSubscriptionRatesSlice.actions;

export * from './adminApi';
export default StudentSubscriptionRatesSlice.reducer;
